<script lang="ts">
import { DPM_STATUS, DPM_STATUS_OPTIONS, LANG_EN, REQUEST_STATUS, REQUEST_STATUS_OPTIONS } from '@/app/const';
import { ComplaintInfo } from '@/interface/Complaint.interface';
import { FormStep, FormStepItem } from '@/interface/Form.interface';
import { complaintService, formService, userService } from '@/services/serviceProvider';
import ResponsibleModal from './responsibleModal.vue';
import { BaseConfirm } from '@/interface/BaseConfirm.interface';
import { ResponsiblePerson } from '@/interface/ResponsiblePerson.interface';
import { SelectItemType } from '@/app/common/types/selectItem.type';
import { useSharedDataStore } from '@/store/app';
import { BaseAlert } from '@/interface/BaseAlert.interface';
import { useRouter } from 'vue-router';

export default {
    components: {
        ResponsibleModal
    },
    setup() {
        const router = useRouter();
        const userSv = userService;
        // store
        const store = useSharedDataStore();
        return { router, store, userSv }
    },
    props: {
        modelValue: {
            type: Object,
            default: {},
        },
        viewMode: {
            type: Boolean,
            default: false
        },
    },
    emits: [
        'displayAlertDialog',
    ],
    data() {
        return {
            model: this.modelValue as ComplaintInfo,
            formStepComplaint: [] as FormStepItem[],
            requestStatuses: REQUEST_STATUS_OPTIONS,
            dpmStatuses: DPM_STATUS_OPTIONS as SelectItemType[],
            careTaker: undefined as string | undefined,
            displayComplaintDetail: false,
            modal: {
                display: false,
            } as BaseConfirm,
            lang: this.$i18n.locale,
            en: LANG_EN,
        }
    },
    watch: {
        modelValue(newVal, oldVal) {
            if (newVal) {
                this.model = newVal;
            }
        },
        'model': {
            handler(val: any) {
                this.careTaker = this.resposibleFullName();
            }, deep: true
        },
        '$i18n.locale': function (newVal, oldVal) {
            this.lang = newVal;
        }
    },
    computed: {
        isMainDpm(): boolean {
            return this.model.relatedMainDepartment?.departmentCode === userService.currentDepartment();
        },
        isSubDpm(): boolean {
            const dpmCodes = this.model.relatedSubDepartments?.map(o => o.departmentCode);
            const currentDepartment = userService.currentDepartment();
            if (dpmCodes && currentDepartment) return dpmCodes.includes(currentDepartment);
            return false;
        },
        showDpmStatus(): boolean {
            return this.isMainDpm || this.isSubDpm;
        },
        dpmStatusLabel(): SelectItemType | undefined {
            if (this.isMainDpm)
                return this.dpmStatuses.find(o => o.value === this.model.relatedMainDepartment?.departmentStatus);
            else if (this.isSubDpm)
                return this.dpmStatuses.find(o => o.value === this.model.relatedSubDepartments
                    ?.find(s => s.departmentCode === userService.currentDepartment())?.departmentStatus);
            else
                return undefined;
        },
        dpmStatusCanEditResponsible() {
            let editable = false;
            if (this.isMainDpm)
                editable = this.model.status === REQUEST_STATUS.IN_PROGRESS
                    && (this.model.relatedMainDepartment?.departmentStatus !== DPM_STATUS.END
                        && this.model.relatedMainDepartment?.departmentStatus !== DPM_STATUS.NO_PERSON
                        && this.model.relatedMainDepartment?.departmentStatus !== DPM_STATUS.RETURN)
            else if (this.isSubDpm) {
                const sub = this.model.relatedSubDepartments?.find(o => o.departmentCode === userService.currentDepartment());
                editable = this.model.status === REQUEST_STATUS.IN_PROGRESS
                    && (sub?.departmentStatus !== DPM_STATUS.END
                        && sub?.departmentStatus !== DPM_STATUS.NO_PERSON
                        && sub?.departmentStatus !== DPM_STATUS.RETURN)
            }
            return editable
        },
        currentUserIsMainResponsible() {
            let mainResponsiblePerson = undefined as ResponsiblePerson | undefined;
            if (this.isMainDpm) {
                mainResponsiblePerson = this.model.relatedMainDepartment?.responiblePersons?.find(o => o.isMainResponsible)
            } else if (this.isSubDpm) {
                mainResponsiblePerson = this.model.relatedSubDepartments
                    ?.find(o => o.departmentCode === userService.currentDepartment())
                    ?.responiblePersons?.find(o => o.isMainResponsible)
            }
            return userService.currentUserInfo()?.employeeId === mainResponsiblePerson?.employeeId;
        },
        currentUserIsActiveResponsible() {
            let activePerson = undefined as ResponsiblePerson | undefined;
            if (this.isMainDpm) {
                activePerson = this.model.relatedMainDepartment?.responiblePersons?.find(o => o.activeResponsible)
            } else if (this.isSubDpm) {
                activePerson = this.model.relatedSubDepartments
                    ?.find(o => o.departmentCode === userService.currentDepartment())
                    ?.responiblePersons?.find(o => o.activeResponsible)
            }
            return userService.currentUserInfo()?.employeeId === activePerson?.employeeId;
        },
        mainUserIsActiveResponsible() {
            let mainResponsiblePerson = undefined as ResponsiblePerson | undefined;
            let activePerson = undefined as ResponsiblePerson | undefined;
            if (this.isMainDpm) {
                mainResponsiblePerson = this.model.relatedMainDepartment?.responiblePersons?.find(o => o.isMainResponsible)
                activePerson = this.model.relatedMainDepartment?.responiblePersons?.find(o => o.activeResponsible)
            } else if (this.isSubDpm) {
                mainResponsiblePerson = this.model.relatedSubDepartments
                    ?.find(o => o.departmentCode === userService.currentDepartment())
                    ?.responiblePersons?.find(o => o.isMainResponsible)
                activePerson = this.model.relatedSubDepartments
                    ?.find(o => o.departmentCode === userService.currentDepartment())
                    ?.responiblePersons?.find(o => o.activeResponsible)
            }
            return activePerson?.employeeId === mainResponsiblePerson?.employeeId;
        },
        isAppeal() {
            return this.model.status === REQUEST_STATUS.APPEAL
        },
        isDraft() {
            return this.model.status === REQUEST_STATUS.DRAFT
        },
        isAscWithResponsible(): boolean {
            if (userService.isAsc()) {
                let responsible = undefined as string | undefined;
                if (this.isMainDpm && this.model?.relatedMainDepartment?.departmentCode === 'ASC') {
                    responsible = this.model.relatedMainDepartment?.responiblePersons?.find(o => o.activeResponsible)?.employeeId
                } else if (this.isSubDpm && this.model?.relatedSubDepartments?.map(o => o.departmentCode).includes('ASC')) {
                    responsible = this.model.relatedSubDepartments.find(o => o.departmentCode === 'ASC')?.responiblePersons?.find(rs => rs.activeResponsible)?.employeeId;
                }
                return responsible === userService.currentUserInfo()?.employeeId;
            }
            return false;
        }
    },
    async mounted() {
        await this.getFormStepComplaint();
        this.careTaker = this.resposibleFullName();
    },
    methods: {
        async getFormStepComplaint() {
            try {
                const res = await formService.getAllSteps();
                if (res && res.status == 200) {
                    const result = res.data as FormStep;
                    this.formStepComplaint = result.stepThree;
                }
            } catch (e: any) {
                throw e;
            }
        },
        async updateResposiblePerson(newResponsibleId: string) {
            try {
                if (this.model.requestNo) {
                    const currentDpm = userService.currentDepartment();
                    let item = {} as any;
                    if (this.isMainDpm) {
                        let newResponsiblePerson = this.model.relatedMainDepartment?.responiblePersons?.find(o => o.employeeId === newResponsibleId);
                        item.departmentCode = this.model.relatedMainDepartment?.departmentCode;
                        item.departmentType = "main";
                        item.employeeId = newResponsibleId;
                        item.title = newResponsiblePerson?.title;
                        item.firstName = newResponsiblePerson?.firstName;
                        item.lastName = newResponsiblePerson?.lastName;
                    }
                    else if (this.isSubDpm && currentDpm) {
                        let dpm = this.model.relatedSubDepartments
                            ?.find(o => o.departmentCode === currentDpm);
                        let newResponsiblePerson = dpm?.responiblePersons?.find(r => r.employeeId === newResponsibleId);
                        item.departmentCode = dpm?.departmentCode;
                        item.departmentType = "sub";
                        item.employeeId = newResponsibleId;
                        item.title = newResponsiblePerson?.title;
                        item.firstName = newResponsiblePerson?.firstName;
                        item.lastName = newResponsiblePerson?.lastName;
                    }
                    if (item.employeeId) {
                        const res = await complaintService.updateResponsiblePerson(this.model.requestNo, item);
                        if (res && res.status == 200) {
                            if (this.isMainDpm) {
                                this.model.relatedMainDepartment?.responiblePersons?.forEach(o => {
                                    o.activeResponsible = false;
                                })
                                let newResponsiblePerson = this.model.relatedMainDepartment?.responiblePersons?.find(o => o.employeeId === newResponsibleId);
                                if (newResponsiblePerson) newResponsiblePerson.activeResponsible = true;
                            } else if (this.isSubDpm) {
                                let dpms = this.model.relatedSubDepartments
                                    ?.find(o => o.departmentCode === currentDpm);
                                dpms?.responiblePersons?.forEach(o => {
                                    o.activeResponsible = false;
                                })
                                let newResponsiblePerson = dpms?.responiblePersons?.find(r => r.employeeId === newResponsibleId);
                                if (newResponsiblePerson) newResponsiblePerson.activeResponsible = true;
                            }
                            this.displayAlertDialog({
                                success: true,
                                messageTitle: this.$t('t-admin-complaint-caretaker-edit-success'),
                                callBack: this.goToListPage
                            } as BaseAlert)
                        }
                    }
                }
            } catch (e: any) {
                this.displayAlertDialog({
                    success: false,
                    messageTitle: this.$t('t-admin-complaint-caretaker-edit-fail'),
                } as BaseAlert)
            }
        },
        goToListPage() {
            this.router.push({
                path: `/complaint-list`
            })
        },
        openRequest(requestNo: string) {
            const routeData = this.$router.resolve({ path: `/complaint/${requestNo}` });
            window.open(routeData.href, '_blank');
        },
        displayAlertDialog(model: BaseAlert) {
            this.$emit('displayAlertDialog', model);
        },
        resposibleFullName() {
            let activePerson = undefined as ResponsiblePerson | undefined;
            if (this.isMainDpm) {
                activePerson = this.model.relatedMainDepartment?.responiblePersons?.find(o => o.activeResponsible)
            } else if (this.isSubDpm) {
                activePerson = this.model.relatedSubDepartments
                    ?.find(o => o.departmentCode === userService.currentDepartment())
                    ?.responiblePersons?.find(o => o.activeResponsible)
            }
            if (activePerson)
                return `${activePerson?.title} ${activePerson?.firstName} ${activePerson?.lastName}`;
            return undefined;
        },
        viewComplaintDetail(detail: string | undefined) {
            if (detail && detail.length > 200) {
                this.displayComplaintDetail = true;
            }
        },
        showEditButton(): boolean {
            let docCanEdit = !this.viewMode && this.dpmStatusCanEditResponsible;
            return (docCanEdit && this.isAscWithResponsible)
                || (docCanEdit && this.currentUserIsActiveResponsible)
                || (this.dpmStatusCanEditResponsible && this.currentUserIsMainResponsible && !this.mainUserIsActiveResponsible);
        }
    },
};
</script>
<template>
    <v-card v-if="model" class="app-card app-form-input pa-0">
        <v-card-text>
            <v-row class="request-header top px-6 d-flex flex-column">
                <div v-if="!isDraft" class="d-flex align-center justify-space-between px-3 mt-3 mb-2">
                    <div class="d-flex text-label-large align-center">
                        <span class="mr-2">{{ $t('t-complaint-request-no') }}</span>
                        <v-chip density="default">
                            <span style="font-weight: 500;">{{ model.complaintNo }}</span>
                        </v-chip>
                    </div>
                </div>
                <div class="d-flex justify-space-between align-center flex-wrap">
                    <v-col cols="auto" class="subject d-flex align-end pl-3">
                        <div class="text-label-large mr-2 mb-0">{{ $t('t-complaint-subject-full') }}</div>
                        <div class="text-value-large mr-3 mb-0">
                            {{ lang === en ? model.mainTopicEn : model.mainTopicTh }}
                        </div>
                        <div class="splitter mr-3 mb-0"></div>
                        <div class="text-label-large mr-2 mb-0">{{ $t('t-complaint-detail') }}</div>
                        <div class="text-value-large mb-0">
                            {{ lang === en ? model.subTopicEn : model.subTopicTh }}
                        </div>
                    </v-col>
                    <v-col cols="auto" class="d-flex align-center">
                        <div v-if="!showDpmStatus" class="text-label-large mr-2 mb-0">{{ $t('t-status') }}</div>
                        <div v-else class="text-label-large mr-2 mb-0">{{ $t('t-req-status') }}</div>
                        <v-chip variant="flat" class="mr-3"
                            :color="requestStatuses.find(o => o.value === model?.status)?.color">
                            {{$t(`t-${requestStatuses.find(o => o.value === model?.status)?.label}`)}}
                        </v-chip>
                        <div v-if="showDpmStatus" class="splitter mr-3"></div>
                        <div v-if="showDpmStatus" class="text-label-large mr-2 mb-0">{{ $t('t-dpm-status') }}</div>
                        <v-chip v-if="showDpmStatus" variant="flat" :color="dpmStatusLabel?.color">
                            {{ $t(`t-${dpmStatusLabel?.label}`) }}
                        </v-chip>
                    </v-col>
                </div>
            </v-row>
        </v-card-text>
        <v-card-text class="request-header data-row">
            <div class="d-flex align-center justify-space-between px-6">
                <div class="text-label-large mb-0">{{ $t('t-complaint-request') }}</div>
                <div class="d-flex-align-center">
                    <v-chip v-if="model.detail.claims" v-for="(claim, index) in model.detail.claims"
                        :class="index != model.detail.claims.length - 1 ? 'mr-2' : ''">
                        <div v-if="formStepComplaint.find(o => o.id === claim.claimsNo)" class="mr-1">
                            {{$t(`t-${formStepComplaint.find(o => o.id === claim.claimsNo)?.name}`)}}
                        </div>
                        <!-- <div v-if="claim.claimsNo === 13" style="font-weight: 300;">({{ claim.remark }})</div> -->
                    </v-chip>
                </div>
            </div>
        </v-card-text>
        <v-card-text v-if="isMainDpm || isSubDpm" class="request-header data-row">
            <div class="d-flex align-center justify-space-between px-6 pb-1">
                <div class="text-label-large mb-0">{{ $t('t-admin-complaint-caretaker') }}</div>
                <div class="d-flex align-center">
                    <span class="mr-1">{{ careTaker }}</span>
                    <v-btn v-if="showEditButton()" variant="text" color="primary" size="x-small"
                        @click="modal.display = true">
                        <v-tooltip v-if="currentUserIsMainResponsible && !mainUserIsActiveResponsible"
                            location="bottom left">
                            <template v-slot:activator="{ props }">
                                <v-icon v-bind="props" icon="mdi mdi-refresh" size="20"></v-icon>
                            </template>
                            <span>{{ $t('t-admin-complaint-caretaker-change') }}</span>
                        </v-tooltip>
                        <v-icon v-else icon="mdi mdi-pencil-outline" size="20"></v-icon>
                    </v-btn>
                </div>
            </div>
        </v-card-text>
    </v-card>
    <ResponsibleModal v-model="modal.display" :complaint="model" @on-confirm="updateResposiblePerson">
    </ResponsibleModal>
</template>